import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as shape from 'd3-shape';
import { combineLatest } from 'rxjs';
import { AuthService } from '../../../abstraction/auth.service';
import { CryptoService } from '../../../abstraction/crypto.service';
import { ICharge, CHARGE_STATUS, CHARGE_STATUS_TEXT } from '../../../models/Commerce';
import { ICryptoPortfolio, ICryptoPrices } from '../../../models/Crypto';
import { SocketService } from '../../../services/socket.service';
import { DataLayerService } from '../../../services/utils/data-layer.service';
import { Utils } from '../../../utils/Utils';
import { ChargeInfoDialogComponent } from './charge-info-dialog/charge-info-dialog.component';
import { Color, ScaleType } from '@swimlane/ngx-charts';

@Component({
  selector: 'app-commerce-dash',
  templateUrl: './commerce-dash.component.html',
  styleUrls: ['./commerce-dash.component.scss'],
})
export class CommerceDashComponent implements OnInit {
  periodOptions = {
    daily: 'Hoy',
    weekly: 'últ. 7 días',
    monthly: 'últ. 30 días',
    yearly: 'últ. año',
  };
  perdiodName = {
    daily: 'diario',
    weekly: 'semanal',
    monthly: 'mensual',
    yearly: 'anual',
  };
  chargeStatusText = CHARGE_STATUS_TEXT;
  remainingChargeAmounts: {
    [id: string]: { amount: number; currency: string };
  };

  //OVERVIEW
  xAxisTicks = [];
  selectedOverviewPeriod = 'daily';
  isOverviewPeriodsOpened = false;
  curve = shape.curveLinear;
  minMax = {
    min: 0,
    max: 0,
  };
  fiatModified = false;
  lastPaymentAmount = 0;
  paymentsInTimeframeSum = 0;
  lastPaymentCurrency = 'AR$';
  history: { name: string; series: { name: string; value: number }[] }[] = [
    {
      name: 'U$D',
      series: [],
    },
  ];
  activeEntries = [];
  chartPointsQuantity = 24;
  colorScheme: Color = {
    name: 'chartScheme',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#EF7BE3', '#ff0000'],
  };
  cryptoBalanceInUsd = 0;

  //PORTFOLIO
  cryptoPortfolio: ICryptoPortfolio[] = [];
  isPortfolioOpened = false;

  //PROCESED PAYMENTS
  selectedPaymentsPeriod = 'daily';
  isPaymentsPeriodsOpened = false;
  charges: ICharge[] = [];
  selectedPaymentsPage = 1;
  paymentsPages = [];

  constructor(
    public cryptoSvc: CryptoService,
    public authSvc: AuthService,
    public socketSvc: SocketService,
    private dialog: MatDialog,
    public dataLayer: DataLayerService,
  ) {}

  ngOnInit(): void {
    this.authSvc.myCryptoPortfolio.subscribe((cryptoPortfolio) => {
      this.cryptoPortfolio = cryptoPortfolio.filter((crypto) => {
        return Number(crypto.balanceArs) >= 1;
      });
    });
    this.charges = [];
    this.history[0].series = [];

    //VERIFICAR SI LO SELECCIONADO COINCIDE CON LA INFO ALMACENADA
    const prevCharges = this.cryptoSvc.getCharges().getValue();
    const prevChargesStats = this.cryptoSvc.getChargesStats().getValue();
    if (
      !prevChargesStats ||
      prevChargesStats.timeFrame.toLocaleLowerCase() !== this.selectedOverviewPeriod
    ) {
      this.cryptoSvc.getChargesStatsByPeriodicity(this.selectedPaymentsPeriod);
    }

    if (!prevCharges || prevCharges.result.periodicity !== this.selectedPaymentsPeriod) {
      this.cryptoSvc.getChargesByPeriodicity(
        this.selectedPaymentsPeriod,
        this.selectedPaymentsPage,
      );
    }

    this.cryptoSvc.getCharges().subscribe((res) => {
      const result = res?.result;
      const page = res?.page;
      if (result && result.periodicity === this.selectedPaymentsPeriod) {
        const pageQuantity = result.pageCount;
        this.paymentsPages = [];
        for (let i = 1; i <= pageQuantity; i++) {
          this.paymentsPages.push(i);
        }
        this.charges = result.charges;

        this.selectedPaymentsPage = page;
      }
      this.getChargesWithRemainingAmount();
    });

    combineLatest([this.cryptoSvc.getChargesStats(), this.cryptoSvc.prices]).subscribe((res) => {
      const charges = res[0];
      const prices = res[1];
      if (
        charges &&
        charges.timeFrame.toLowerCase() === this.selectedOverviewPeriod &&
        prices.DAI_ARS?.sell
      ) {
        this.lastPaymentCurrency = this.getCurrencySymbol(charges.lastPaymentCurrency);
        this.lastPaymentAmount = charges.lastPaymentAmount;
        this.paymentsInTimeframeSum = charges.paymentsInTimeframeSum;
        const period = charges.timeFrame.toLowerCase();
        const seriesNames = [];
        const current = new Date();
        const stats = [...charges.statsInDivision];

        //Obtener etiquetas del eje horizontal del chart
        if (period === 'weekly') {
          this.chartPointsQuantity = 7;
          for (let i = 0; i <= this.chartPointsQuantity; i++) {
            const day = new Date();
            const daysAgo = i * 1000 * 60 * 60 * 24;
            day.setTime(day.getTime() - daysAgo);
            seriesNames.push(day.toLocaleString('es-ar', { day: '2-digit', month: 'numeric' }));
          }
        } else if (period === 'monthly') {
          this.chartPointsQuantity = 4;
          const weekMs = 1000 * 60 * 60 * 24 * 7;
          for (let i = 0; i <= this.chartPointsQuantity; i++) {
            const referenceDay = new Date(current.getTime() - i * weekMs);
            const firstDayWeek = new Date();
            const weeksAgo = (i + 1) * weekMs;
            firstDayWeek.setTime(firstDayWeek.getTime() - weeksAgo);
            let firstDayWeekString = firstDayWeek.toLocaleString('es-ar', { day: '2-digit' });
            let lastDayWeekString = referenceDay.toLocaleString('es-ar', { day: '2-digit' });
            let serieName = '';
            firstDayWeekString = firstDayWeek.toLocaleString('es-ar', {
              day: '2-digit',
              month: 'numeric',
            });
            lastDayWeekString = referenceDay.toLocaleString('es-ar', {
              day: '2-digit',
              month: 'numeric',
            });
            serieName = firstDayWeekString + ' - ' + lastDayWeekString;
            seriesNames.push(serieName);
          }
        } else if (period === 'yearly') {
          this.chartPointsQuantity = 12;
          for (let i = 0; i <= this.chartPointsQuantity; i++) {
            const month = new Date();
            month.setDate(28); //Para febrero
            const monthNumber = current.getMonth() - i;
            if (monthNumber < 0) {
              month.setMonth(monthNumber + 12);
              month.setFullYear(month.getFullYear() - 1);
            } else {
              month.setMonth(monthNumber);
            }
            seriesNames.push(month.toLocaleString('es-ar', { month: 'short' }));
          }
        } else {
          //DAYLY
          this.chartPointsQuantity = 24;
          for (let i = 0; i <= this.chartPointsQuantity; i++) {
            const day = new Date();
            day.setHours(23);
            day.setMinutes(0);
            const hoursAgo = i * 1000 * 60 * 60;
            day.setTime(day.getTime() - hoursAgo);
            seriesNames.push(day.toLocaleString('es-ar', { hour: '2-digit', minute: '2-digit' }));
          }
        }
        this.xAxisTicks = seriesNames;
        this.history[0].series = stats
          .reverse()
          .slice(0, seriesNames.length - 1)
          .map((value, index) => {
            return { name: seriesNames[index] || '-', value: value };
          })
          .reverse();

        this.history = [...this.history]; //Refresh chart

        this.minMax = Utils.getHistoryMinMax(this.history);

        this.cryptoSvc.setPortfolioByCryptoBalance();
        this.getChargesWithRemainingAmount();
        this.getUsdCryptoBalance(prices);
      }
    });

    this.cryptoSvc.prices.subscribe((prices) => {
      this.getUsdCryptoBalance(prices);
    });
  }

  //OVERVIEW

  selectOverviewPeriod(period: string) {
    if (period !== this.selectedOverviewPeriod) {
      this.selectedOverviewPeriod = period;
      this.showOverviewPeriodsOptions(false);
      this.cryptoSvc.getChargesStatsByPeriodicity(this.selectedOverviewPeriod);
    }
  }

  showOverviewPeriodsOptions(open: boolean) {
    this.isOverviewPeriodsOpened = open;
  }

  //PORTFOLIO
  openPortfolio(open: boolean) {
    this.isPortfolioOpened = open;
  }

  getUsdCryptoBalance(prices: ICryptoPrices) {
    this.cryptoBalanceInUsd = 0;
    const portfolio = this.authSvc.loggedUser?.balance?.crypto;
    if (portfolio) {
      const portfolioCoins = Object.keys(portfolio);
      let totalUsd = 0;
      let totalArs = 0;
      if (Object.keys(prices).length !== 0) {
        portfolioCoins.forEach((coin) => {
          const priceObj = prices[coin + '_USD'];
          const priceArsObj = prices[coin + '_ARS'];
          if (priceObj) {
            if (coin === 'AUST') {
              totalUsd += portfolio[coin].amount * +prices.AUST_DAI.sell * +prices.DAI_USD.sell;
            } else {
              totalUsd += portfolio[coin].amount * +prices[coin + '_USD'].sell;
            }
          }
          if (priceArsObj) {
            if (coin === 'AUST') {
              totalArs += portfolio[coin].amount * +prices.AUST_DAI.sell * +prices.DAI_ARS.sell;
            } else {
              totalArs += portfolio[coin].amount * +prices[coin + '_ARS'].sell;
            }
          }
        });
        this.cryptoBalanceInUsd = totalUsd;
      }
    }
  }

  //PROCESED PAYMENTS

  selectPaymentsPeriod(period: string) {
    if (this.selectedPaymentsPeriod !== period) {
      this.selectedPaymentsPeriod = period;
      this.showPaymentsPeriodsOptions(false);
      this.cryptoSvc.getChargesByPeriodicity(this.selectedPaymentsPeriod, 1);
    }
  }

  showPaymentsPeriodsOptions(open: boolean) {
    this.isPaymentsPeriodsOpened = open;
  }

  selectPaymentsPage(page: number) {
    if (page >= 1 && page <= this.paymentsPages.length) {
      this.cryptoSvc.getChargesByPeriodicity(this.selectedPaymentsPeriod, page);
    }
  }

  openChartDetails(chargeId: string) {
    this.dialog.open(ChargeInfoDialogComponent, {
      backdropClass: 'dialog-primary-backdrop',
      panelClass: 'deposit',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        chargeId,
      },
    });
  }

  addDolarSymbolToCoinName(value) {
    return value.replace('S', '$');
  }

  getChargesWithRemainingAmount() {
    this.charges.forEach((charge) => {
      if (charge.status === CHARGE_STATUS.UNDERPAID) {
        const totalAmount = +charge.price.amount;
        const currency = charge.price.currency;
        const payments = charge.payments.filter((payment) => payment.status === 'confirmed');
        if (payments.length) {
          const totalPaid = payments.reduce((prev, curr) => prev + +curr.value.human, 0);
          let remainingAmount = totalAmount - totalPaid;

          if (currency === 'STABLES') {
            remainingAmount = totalAmount - totalPaid;
          } else {
            const fiatQuote = +this.cryptoSvc.prices.value['DAI_' + currency]?.sell;
            remainingAmount = totalAmount / fiatQuote - totalPaid;
          }

          this.remainingChargeAmounts = {
            ...this.remainingChargeAmounts,
            [charge.id]: {
              amount: remainingAmount,
              currency: payments[0].coin,
            },
          };
        }
      }
    });
  }

  getFormattedDate(time: string) {
    const currenct = new Date();
    const chargeCreation = new Date(time);
    const date =
      chargeCreation.toLocaleDateString() === currenct.toLocaleDateString()
        ? 'Hoy'
        : chargeCreation.toLocaleDateString('es-ar', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
    const hoursMinutes = chargeCreation.toLocaleString('es-ar', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return date + ' ' + hoursMinutes;
  }
  getCurrencySymbol(currency: string): string {
    switch (currency) {
      case 'ARS':
        return 'AR$';
      case 'STABLES':
        return '$TABLES';
      default:
        return 'U$D';
    }
  }
}
